import { Project } from 'src/app/core/objects/project';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { Group, WorkSpace } from 'src/app/core/types';

export interface DisplayedWorkSpace {
  workSpace: WorkSpace;
  index: number;
  quantity: number;
  memberIndexes: number[];
  memberWorkSpaceIds: number[];
}

export function toDisplayedWorkSpaces(
  workSpaces: WorkSpace[] | undefined | null,
): DisplayedWorkSpace[] {
  return (workSpaces ?? []).map((workSpace, index) => ({
    workSpace,
    index,
    quantity: palletSpaceQuantity(workSpace) || 1,
    memberIndexes: [index],
    memberWorkSpaceIds: [workSpace.id],
  }));
}

export function palletSpaceQuantity(workSpace: WorkSpace | undefined | null): number {
  const cargoSpace = workSpace?.cargoSpaces?.[0];
  if (cargoSpace?.type !== LoadingSpaceType.Pallet) {
    return 0;
  }
  const quantity = cargoSpace.extraFeatures?.palletQuantity;
  return quantity != null && quantity > 0 ? quantity : 1;
}

/** Pallet rows count as their quantity; every other workspace counts as one. */
export function displayedLoadingSpaceCount(workSpaces: WorkSpace[] | undefined | null): number {
  return (workSpaces ?? []).reduce(
    (sum, workSpace) => sum + (palletSpaceQuantity(workSpace) || 1),
    0,
  );
}

/** `on pallet / total across replicas`, or null when the pallet is not replicated. */
export function replicatedLoadCountLabel(
  onPallet: number,
  palletQuantity: number,
): string | null {
  if (palletQuantity <= 1) {
    return null;
  }
  return `${onPallet} / ${onPallet * palletQuantity}`;
}

export function applyPalletWorkspaceQuantityToVehicleLoads(
  groups: Group[],
  workSpaces: WorkSpace[] | undefined | null,
): Group[] {
  if (!groups.length) {
    return groups;
  }

  const quantityByGroupId = new Map<number, number>();
  for (const workSpace of workSpaces ?? []) {
    const extraFeatures = workSpace?.cargoSpaces?.[0]?.extraFeatures;
    const groupId = extraFeatures?.palletIdInVehicle;
    const quantity = palletSpaceQuantity(workSpace);
    if (groupId == null || quantity <= 1) {
      continue;
    }
    quantityByGroupId.set(groupId, Math.max(quantityByGroupId.get(groupId) ?? 0, quantity));
  }

  if (!quantityByGroupId.size) {
    return groups;
  }

  let changed = false;
  const next = groups.map((group) => {
    const quantity = quantityByGroupId.get(group.id);
    if (quantity == null || quantity <= (group.quantity ?? 0)) {
      return group;
    }
    changed = true;
    return { ...group, quantity };
  });
  return changed ? next : groups;
}

export function palletQuantityDifferenceToRestoreOnVehicle(
  project: Project | undefined | null,
  palletWorkSpaceId: number | undefined,
  previousQuantity: number,
): number {
  if (previousQuantity <= 1 || palletWorkSpaceId == null) {
    return 0;
  }

  const workSpace = project?.workSpaces?.find((space) => space.id === palletWorkSpaceId);
  const extraFeatures = workSpace?.cargoSpaces?.[0]?.extraFeatures;
  if (!extraFeatures?.palletVehicle || extraFeatures.palletIdInVehicle == null) {
    return 0;
  }

  const currentPalletQuantity = palletSpaceQuantity(workSpace);
  const groupQuantity = vehiclePalletGroupQuantity(
    project,
    extraFeatures.palletVehicle,
    extraFeatures.palletIdInVehicle,
  );
  if (groupQuantity == null || currentPalletQuantity !== groupQuantity) {
    return 0;
  }
  return Math.max(0, previousQuantity - groupQuantity);
}

export function restorePalletWorkspaceQuantity(
  project: Project | undefined | null,
  palletWorkSpaceId: number | undefined,
  quantity: number,
): Project | undefined | null {
  if (!project || palletWorkSpaceId == null || quantity <= 1) {
    return project;
  }
  const workSpace = project.workSpaces?.find((space) => space.id === palletWorkSpaceId);
  const cargoSpace = workSpace?.cargoSpaces?.[0];
  if (cargoSpace?.type !== LoadingSpaceType.Pallet) {
    return project;
  }
  cargoSpace.extraFeatures = {
    ...(cargoSpace.extraFeatures ?? {}),
    palletQuantity: quantity,
  };
  const groupId = cargoSpace.extraFeatures.palletIdInVehicle;
  const vehicleCargoSpaceId = cargoSpace.extraFeatures.palletVehicle;
  if (groupId == null || !vehicleCargoSpaceId) {
    return project;
  }
  for (const space of project.workSpaces ?? []) {
    for (const vehicleCargoSpace of space.cargoSpaces ?? []) {
      if (vehicleCargoSpace.id !== vehicleCargoSpaceId) {
        continue;
      }
      const group = vehicleCargoSpace.groups?.find((item) => item.id === groupId);
      if (group && (group.quantity ?? 0) < quantity) {
        group.quantity = quantity;
      }
    }
  }
  return project;
}

function vehiclePalletGroupQuantity(
  project: Project | undefined | null,
  cargoSpaceId: number,
  groupId: number,
): number | null {
  for (const workSpace of project?.workSpaces ?? []) {
    for (const cargoSpace of workSpace.cargoSpaces ?? []) {
      if (cargoSpace.id !== cargoSpaceId) {
        continue;
      }
      const group = cargoSpace.groups?.find((item) => item.id === groupId);
      return group?.quantity ?? null;
    }
  }
  return null;
}
